import React, { useState } from 'react';
import { toast } from 'sonner';
import { Check, ShieldCheck, Star, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Card, Pill, PrimaryButton, SectionHeader } from './ui';
import { inr, PROGRAM_NAMES, studentApi } from '../../pages/journey/journeyApi';

const priceOf = (o) => (o.upgrade ? o.upgrade.upgrade_price : o.price);

const Offer = ({ o, selected, onSelect }) => (
  <button onClick={() => !o.owned && onSelect(o.id)} disabled={o.owned} data-testid={`offer-${o.id}`}
    className={`flex flex-col justify-start text-left rounded-2xl border-2 p-6 bg-white transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${selected ? 'border-teal-500' : 'border-slate-200'} ${o.owned ? 'opacity-70 cursor-default' : ''}`}>
    <div className="flex items-center gap-2 mb-3 min-h-[26px]">
      {o.recommended && <Pill className="bg-teal-500 text-white" data-testid={`offer-recommended-${o.id}`}><Star className="w-3 h-3" /> Recommended for you</Pill>}
      {!o.recommended && o.suggested && <Pill className="bg-teal-50 text-teal-700">Also suitable</Pill>}
      {o.owned && <Pill className="bg-emerald-100 text-emerald-800" data-testid={`offer-owned-${o.id}`}><Check className="w-3 h-3" /> Enrolled</Pill>}
    </div>
    <p className="font-outfit font-semibold text-lg text-[#0b1f4d]">{o.name}</p>
    <p className="text-sm text-slate-600 mt-1 min-h-[60px]">{o.tagline}</p>
    <div className="mt-4">
      {o.upgrade && !o.owned ? (<>
        <p className="text-sm text-slate-400 line-through">{inr(o.price)}</p>
        <p className="font-outfit text-3xl font-bold text-[#0b1f4d]" data-testid={`offer-price-${o.id}`}>{inr(o.upgrade.upgrade_price)}</p>
        <p className="text-xs text-teal-700">Upgrade: {inr(o.upgrade.credit)} credit from your earlier purchase</p>
      </>) : (<>
        <p className="font-outfit text-3xl font-bold text-[#0b1f4d]" data-testid={`offer-price-${o.id}`}>{inr(o.price)}</p>
        {o.saving > 0 && <p className="text-xs text-teal-700" data-testid="offer-integrated-saving">{inr(o.individual_total)} separately · save {inr(o.saving)}</p>}
      </>)}
      <p className="text-xs text-slate-500 mt-1">One-time payment · no subscription</p>
    </div>
    <ul className="mt-4 space-y-1 text-xs text-slate-600">
      {o.includes.length > 1 && o.includes.map((i) => <li key={i}>✓ {PROGRAM_NAMES[i]}</li>)}
      <li>✓ Lifetime access to DIY modules (platform terms)</li>
      <li>✓ {o.support.weeks}-week support · {o.support.checkins} weekly check-ins</li>
      <li>✓ {o.support.milestone_reviews} milestone review(s){o.support.specialist_reviews ? ` · ${o.support.specialist_reviews} specialist review` : ''}</li>
    </ul>
  </button>
);

const Checkout = ({ offer, studentId, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      await studentApi('/me/checkout', { method: 'POST', body: { student_id: studentId, program_id: offer.id } });
      toast.success(`Enrolled in ${offer.name}`); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!offer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkout-dialog">
        <DialogHeader><DialogTitle className="font-outfit">Demo checkout</DialogTitle>
          <DialogDescription>This is a simulated payment. No money is collected and Razorpay is not called.</DialogDescription></DialogHeader>
        {offer && <div className="rounded-xl bg-slate-50 p-4 text-sm">
          <div className="flex justify-between"><span>{offer.name}</span><span className="font-semibold" data-testid="checkout-amount">{inr(priceOf(offer))}</span></div>
          {offer.upgrade && <p className="text-xs text-slate-500 mt-1">{inr(offer.price)} − {inr(offer.upgrade.credit)} credit</p>}
        </div>}
        <PrimaryButton onClick={pay} disabled={busy} data-testid="checkout-confirm-btn" className="w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Simulate successful payment
        </PrimaryButton>
      </DialogContent>
    </Dialog>
  );
};

export const Programs = ({ data: d, refresh, studentId, go }) => {
  const rec = d.summary?.primary;
  const [selected, setSelected] = useState(rec);
  const [checkout, setCheckout] = useState(null);
  const sel = d.offers.find((o) => o.id === selected && !o.owned);
  const enroll = () => (d.is_demo ? setCheckout(sel) : window.location.assign(`/apply?plan=${sel.package_id}`));
  return (
    <>
      <SectionHeader eyebrow="One-time payments" title="Programs & Enrollment">{d.summary ? "We highlight what fits you best, but you're free to choose any individual program." : 'Preview every program. Your consultant will recommend the best fit after your Career Clarity Consultation, or you can enroll in any program now.'}</SectionHeader>
      {!d.summary && <Card className="mb-5 py-4 text-sm text-slate-600" data-testid="programs-no-recommendation"><strong className="text-[#0b1f4d]">Personal recommendation comes after your consultation.</strong> Learning content unlocks only for the program you pay for.</Card>}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="offers-grid">
        {d.offers.map((o) => <Offer key={o.id} o={o} selected={selected === o.id} onSelect={setSelected} />)}
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <PrimaryButton disabled={!sel} onClick={enroll} data-testid="enroll-selected-btn">
          {sel ? `Enroll in ${sel.name} · ${inr(priceOf(sel))}` : 'Select a program'}
        </PrimaryButton>
        {d.entitlements.length > 0 && <button onClick={() => go('learning')} className="text-sm font-semibold text-teal-700" data-testid="go-learning-btn">Go to my learning journey →</button>}
      </div>
      <p className="text-xs text-slate-500 mt-4 max-w-3xl" data-testid="access-terms">{d.access_terms}</p>
      {d.enrollments.length > 0 && <Card className="mt-6" data-testid="enrollment-history">
        <p className="font-outfit font-semibold text-[#0b1f4d] mb-3">Enrollment history</p>
        {d.enrollments.map((e) => <div key={e.id} className="flex flex-wrap justify-between gap-2 text-sm py-2 border-t border-slate-100 first:border-0">
          <span>{e.program_name}</span><span className="text-slate-500">{inr(e.amount)} · {e.payment.mode} {e.payment.ref} · {new Date(e.created_at).toLocaleDateString('en-IN')}</span></div>)}
      </Card>}
      <Checkout offer={checkout} studentId={studentId} onClose={() => setCheckout(null)} onDone={() => { setCheckout(null); refresh(); }} />
    </>
  );
};
